import styled from "@emotion/styled";

/**
 * The Grafana-styled chrome shared by the admin Metrics tab and the
 * customer-facing Technical status page.
 *
 * Extracted so the two pages cannot drift: they are meant to look like the same
 * tool, and the customer page is explicitly "the thing we look at, but for you".
 * Anything genuinely specific to one page stays in that page.
 */

// ── Range picker ───────────────────────────────────────────

export const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const TimeRangeDropdownWrap = styled.div`
  position: relative;
  margin-left: auto;
`;

export const TimeRangeButton = styled.button<{ active?: boolean }>`
  height: 36px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${({ active }) => (active ? "#1a202c" : "#fff")};
  color: ${({ active }) => (active ? "#e2e8f0" : "#494c5b")};
  border: 1px solid ${({ active }) => (active ? "#4a5568" : "#ebeeef")};
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${({ active }) => (active ? "#2d3748" : "#f7fafc")};
  }
`;

export const ClockIcon = styled.span`
  font-size: 14px;
  line-height: 1;
`;

export const DropdownArrow = styled.span`
  font-size: 10px;
  color: #a0aec0;
`;

export const TimeRangeMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 260px;
  background: #1a202c;
  border: 1px solid #4a5568;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 100;
  overflow: hidden;
`;

export const TimeRangeMenuHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #2d3748;
`;

export const TimeRangeMenuTab = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 10px 12px;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ active }) => (active ? "#e2e8f0" : "#718096")};
  background: ${({ active }) => (active ? "#2d3748" : "transparent")};
  border: none;
  cursor: pointer;
  &:hover {
    color: #e2e8f0;
  }
`;

export const TimeRangeMenuBody = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

export const TimeRangeMenuAbsolute = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AbsoluteLabel = styled.label`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const AbsoluteInput = styled.input`
  height: 32px;
  width: 100%;
  border: 1px solid #4a5568;
  border-radius: 4px;
  background: #2d3748;
  color: #e2e8f0;
  font-family: monospace;
  font-size: 12px;
  padding: 0 8px;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: #63b3ed;
  }
`;

export const ApplyButton = styled.button`
  height: 32px;
  border: none;
  border-radius: 4px;
  background: #3182ce;
  color: #fff;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #2b6cb0;
  }
`;

export const QuickRangeItem = styled.button<{ selected?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 16px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: ${({ selected }) => (selected ? "#63b3ed" : "#cbd5e0")};
  background: ${({ selected }) => (selected ? "#2d3748" : "transparent")};
  border: none;
  cursor: pointer;
  &:hover {
    background: #2d3748;
    color: #63b3ed;
  }
`;

// ── Map panel ──────────────────────────────────────────────

export const MapWrap = styled.div`
  position: relative;
  height: 400px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
`;

export const MapLayerToolbar = styled.div`
  position: absolute;
  z-index: 10;
  top: 18px;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LayerSelect = styled.select`
  height: 36px;
  min-width: 160px;
  border-radius: 4px;
  border: 1px solid #cbd5d8;
  background: #fff;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 10px;
  color: #494c5b;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  cursor: pointer;
`;

// ── Panels ─────────────────────────────────────────────────

export const MetricsShell = styled.div`
  margin-top: 8px;
  padding: 16px;
  border-radius: 8px;
  background: #111217;
  border: 1px solid #2a2e37;
`;

export const MetricsHeader = styled.div`
  margin-bottom: 16px;
`;

export const MetricsTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #e9edf2;
  margin-bottom: 4px;
`;

export const MetricsSub = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #8e9ba8;
`;

export const Panel = styled.div`
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

export const PanelTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #d8d9da;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

export const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #c7d0d9;
  cursor: pointer;
  user-select: none;
`;

export const ToggleSwatch = styled.span<{ color: string }>`
  width: 14px;
  height: 3px;
  border-radius: 1px;
  background: ${(p) => p.color};
`;

export const ResetZoomBtn = styled.button`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #3d4550;
  background: #1e2229;
  color: #9aa5b1;
  cursor: pointer;
  &:hover {
    background: #262b35;
    color: #e9edf2;
  }
`;

export const HintText = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #6b7785;
`;

export const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

/** Recharts `<Tooltip contentStyle>` matching the dark panels. */
export const tooltipStyle = {
  backgroundColor: "#1e2229",
  border: "1px solid #3d4550",
  borderRadius: 4,
  fontSize: 12,
  color: "#e9edf2",
};
